import { Card, Flex, Progress, Typography } from 'antd';

import { TextQuaternary, TextTertiary } from '../../../ui/components/text';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

export type UsageBreakdownProps = {
  items: GetBillingOverviewResponseDto['usageThisPeriod'];
};

export const UsageBreakdown = ({ items }: UsageBreakdownProps) => {
  const sum = (match: (usageType: string) => boolean, of: 'credits' | 'units') =>
    items.filter((item) => match(item.usageType)).reduce((total, item) => total + item[of], 0);
  const isAi = (usageType: string) => usageType.startsWith('ai_');
  const isReveal = (usageType: string) => usageType === 'data_reveal';
  const isSms = (usageType: string) => usageType.startsWith('sms_');
  const isCall = (usageType: string) =>
    usageType === 'call_outbound' || usageType === 'call_inbound';
  const isTranscription = (usageType: string) => usageType === 'call_recording_transcription';
  const count = (value: number, unit: string) => `${Math.round(value).toLocaleString()} ${unit}`;

  const rows = [
    {
      key: 'ai',
      label: 'AI actions',
      units: count(sum(isAi, 'units'), 'actions'),
      credits: sum(isAi, 'credits'),
      color: 'var(--ant-purple-5)',
    },
    {
      key: 'reveal',
      label: 'Person reveals',
      units: count(sum(isReveal, 'units'), 'records'),
      credits: sum(isReveal, 'credits'),
      color: 'var(--ant-cyan-5)',
    },
    {
      key: 'calls',
      label: 'Calls & texts',
      units: [
        sum(isSms, 'units') > 0 && count(sum(isSms, 'units'), 'messages'),
        sum(isCall, 'units') > 0 && count(sum(isCall, 'units'), 'min'),
      ]
        .filter(Boolean)
        .join(' · '),
      credits: sum((usageType) => isSms(usageType) || isCall(usageType), 'credits'),
      color: 'var(--ant-blue-5)',
    },
    {
      key: 'transcription',
      label: 'Transcription',
      units: count(sum(isTranscription, 'units'), 'min'),
      credits: sum(isTranscription, 'credits'),
      color: 'var(--ant-yellow-5)',
    },
  ].filter((row) => row.credits > 0);
  const total = rows.reduce((value, row) => value + row.credits, 0);

  return (
    <Card style={{ borderRadius: 20, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
      {rows.length === 0 && (
        <Flex style={{ padding: '12px 18px' }}>
          <TextTertiary style={{ fontSize: 13 }}>Nothing used yet this period.</TextTertiary>
        </Flex>
      )}
      {rows.map((row, index) => (
        <Flex
          key={row.key}
          align="center"
          gap={14}
          data-testid={`usage-row-${row.key}`}
          style={{
            padding: '12px 18px',
            borderTop: index > 0 ? '1px solid var(--ant-color-split)' : undefined,
          }}
        >
          <span
            style={{ flex: 'none', width: 8, height: 8, borderRadius: 9999, background: row.color }}
          />
          <Flex vertical gap={5} flex={1}>
            <Flex align="baseline" gap={8}>
              <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                {row.label}
              </Typography.Text>
              <TextQuaternary style={{ fontSize: 12 }}>{row.units}</TextQuaternary>
            </Flex>
            <Progress
              percent={(row.credits / total) * 100}
              showInfo={false}
              size={['100%', 4]}
              strokeColor={row.color}
              railColor="var(--ant-color-fill-tertiary)"
            />
          </Flex>
          <Typography.Text
            className="font-mono"
            style={{ minWidth: 60, textAlign: 'right', fontSize: 13 }}
          >
            {Math.ceil(row.credits).toLocaleString()}
          </Typography.Text>
        </Flex>
      ))}
      {rows.length > 0 && (
        <Flex
          align="center"
          justify="space-between"
          gap={14}
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--ant-color-split)',
            background: 'var(--ant-color-fill-alter)',
          }}
        >
          <Typography.Text strong style={{ fontSize: 13 }}>
            Total used
          </Typography.Text>
          <Typography.Text className="font-mono" strong style={{ fontSize: 13 }}>
            {Math.ceil(total).toLocaleString()}
          </Typography.Text>
        </Flex>
      )}
    </Card>
  );
};
